import { useState } from "react";
import DashboardContainer from "../../../containers/admin";
import Header from "../../../components/admin/header";
import api from "../../../utils/apiConfig";
import { toast } from "react-hot-toast";

const PushCampaigns = () => {
  const [formData, setFormData] = useState({
    title: "",
    body: ""
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Please fill in both title and body fields");
      return;
    }

    setIsLoading(true);
    
    try {
      await api.post("/admin/campaign/push-notification", {
        title: formData.title.trim(),
        body: formData.body.trim()
      });

      toast.success("Push notification campaign sent successfully!");
      
      // Reset form after successful submission
      setFormData({
        title: "",
        body: ""
      });
      
    } catch (error) {
      console.error("Error sending push notification:", error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.status === 401) {
        toast.error("Unauthorized. Please login again.");
      } else if (error.response?.status === 403) {
        toast.error("Access denied. Insufficient permissions.");
      } else {
        toast.error("Failed to send push notification. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardContainer>
      <Header title="Push Notification Campaigns" />
      
      <div className="bg-white p-8 rounded-xl shadow-md max-w-2xl">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Send Push Notification Campaign
          </h2>
          <p className="text-gray-600 text-sm">
            Create and send push notifications to all app users. Make sure your message is clear and engaging.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label 
              htmlFor="title" 
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Notification Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter notification title..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary transition-colors"
              maxLength={60}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.title.length}/60 characters
            </p>
          </div>

          <div>
            <label 
              htmlFor="body" 
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Notification Body *
            </label>
            <textarea
              id="body"
              name="body"
              value={formData.body}
              onChange={handleInputChange}
              placeholder="Enter notification message..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-secondary transition-colors resize-vertical"
              maxLength={240}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.body.length}/240 characters
            </p>
          </div>

          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <p className="font-medium">Preview:</p>
                {formData.title || formData.body ? (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                    <div className="font-semibold text-gray-800">
                      {formData.title || "Notification Title"}
                    </div>
                    <div className="text-gray-600 text-sm mt-1">
                      {formData.body || "Notification body text will appear here..."}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400 mt-2">Fill in the form to see preview</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => setFormData({ title: "", body: "" })}
              className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.title.trim() || !formData.body.trim()}
              className="px-8 py-2 bg-secondary text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <span>Send Campaign</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </DashboardContainer>
  );
};

export default PushCampaigns;